import React, { useEffect, useState } from 'react';
import { Box, Typography, CircularProgress, Button } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchMetalRewardsRequest,
  updateStatusRequest,
} from '../../../redux/MetalRewards/metalRewardsSlice';
import TableCompo from '../Table/TableCompo';
import DynamicDialog from '../../Modals/CommanDialog';
import { canShowPermission } from '../Permissions/permissionObject';

const MetalRewardsList = () => {
  const dispatch = useDispatch();
  const {
    list = [],
    loading,
    error,
    updating,
    total,
  } = useSelector((state) => state.metalRewards);

  const [permissions, setPermissions] = useState({});
  useEffect(() => {
    const stored = localStorage.getItem('userPermissions');
    if (stored) {
      try {
        setPermissions(JSON.parse(stored));
      } catch (e) {
        console.error('Failed to parse userPermissions', e);
      }
    }
  }, []);
  const canEdit = canShowPermission(permissions, 'metalRewards', 'edit');

  const [pagination, setPagination] = useState({ page: 0, limit: 10 });
  const [search, setSearch] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedReward, setSelectedReward] = useState(null);

  useEffect(() => {
    dispatch(
      fetchMetalRewardsRequest({
        start: pagination.page * pagination.limit,
        limit: pagination.limit,
        search,
      }),
    );
  }, [dispatch, pagination, search]);

  const handleTableChange = (action, tableState) => {
    switch (action) {
      case 'changePage':
        setPagination((prev) => ({ ...prev, page: tableState.page }));
        break;
      case 'changeRowsPerPage':
        setPagination({ page: 0, limit: tableState.rowsPerPage });
        break;
      case 'search':
        setSearch(tableState.searchText || '');
        setPagination((prev) => ({ ...prev, page: 0 }));
        break;
      default:
        break;
    }
  };

  const handleStatusClick = (row) => {
    setSelectedReward({
      ...row,
      id: row.id || row._id,
    });
    setOpenDialog(true);
  };

  const handleDialogClose = () => setOpenDialog(false);

  const handleDialogSubmit = () => {
    dispatch(
      updateStatusRequest({
        id: selectedReward.id,
        enabled: !selectedReward.enabled,
      }),
    );

    dispatch(
    fetchMetalRewardsRequest({
      start: pagination.page * pagination.limit,
      limit: pagination.limit,
      search,
    }),
  );
    
    setOpenDialog(false);
  };

  const formattedList = list.map((item) => {
    const buyMetal =
      item.buyMetal && item.buyMetal.metalName
        ? `${item.buyMetal.metalName}-${item.buyMetal.karat}K-${item.buyMetal.purity}`
        : '-';

    const rewardMetal = item.isDiamondPoint
      ? 'Diamond Point'
      : item.rewardMetal && item.rewardMetal.metalName
        ? `${item.rewardMetal.metalName}-${item.rewardMetal.karat}K-${item.rewardMetal.purity}`
        : '-';

    return {
      ...item,
      id: item._id,
      date: item.date?.order
        ? new Date(item.date.order).toLocaleDateString('en-IN')
        : 'N.A',
      startDate: item.startDate
        ? new Date(item.startDate).toLocaleDateString('en-IN')
        : 'N.A',
      endDate: item.endDate
        ? new Date(item.endDate).toLocaleDateString('en-IN')
        : 'N.A',
      buyMetal,
      rewardMetal,
      percentage: item.percentage ?? '-',
      offerOn: item.offerOn || '-',
      offerType: item.offerType || '-',
      diamondPointType: item.diamondPointType || '-',
      diamondPointValue: item.diamondPointValue ?? '-',
      enabled: typeof item.enabled === 'boolean' ? item.enabled : '-',
    };
  });

  const columns = [
    { field: 'id', headerName: 'ID', hide: true, hideInExcel: true },
    { field: 'date', headerName: 'Date' },
    { field: 'startDate', headerName: 'Start Date' },
    { field: 'endDate', headerName: 'End Date' },
    { field: 'buyMetal', headerName: 'Buy Metal' },
    { field: 'rewardMetal', headerName: 'Reward Metal' },
    { field: 'percentage', headerName: 'Percentage' },
    { field: 'offerOn', headerName: 'Offer Type' },
    { field: 'diamondPointType', headerName: 'Diamond Point Type' },
    { field: 'diamondPointValue', headerName: 'Diamond Point Value' },
    {
      field: 'enabled',
      headerName: 'Action',
      render: (value, row) => {
        const isEnabled = value === true;

        if (!canEdit) {
          return (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: 50,
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  color: isEnabled ? '#43a047' : '#e53935',
                  textAlign: 'center',
                }}
              >
                {isEnabled ? 'Enable' : 'Disable'}
              </Typography>
            </Box>
          );
        }
        return (
          <button
            style={{
              backgroundColor: isEnabled ? '#43a047' : '#e53935',
              color: '#fff',
              border: 'none',
              padding: '5px 10px',
              borderRadius: '4px',
              cursor: 'pointer',
            }}
            onClick={() => handleStatusClick(row)}
          >
            {isEnabled ? 'Enable' : 'Disable'}
          </button>
        );
      },
    },
  ];

  return (
    <Box p={2}>
      <Typography fontWeight={700} fontSize={24} fontFamily={'Aeonik'}>
        Metal Rewards List
      </Typography>

      {loading ? (
        <Box display="flex" justifyContent="center" my={4}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Typography color="error">{error}</Typography>
      ) : formattedList.length === 0 ? (
        <Typography>No Metal Rewards found</Typography>
      ) : (
        <>
          <TableCompo
            data={formattedList}
            columns={columns}
            totalCount={total}
            onTableChange={handleTableChange}
            page={pagination.page}
            limit={pagination.limit}
          />
          {selectedReward && (
            <DynamicDialog
              open={openDialog}
              onClose={handleDialogClose}
              onSubmit={handleDialogSubmit}
              title="Change Metal Status"
              message="Are you sure you want to change the status of this metal reward?"
              cancelText="Cancel"
              submitText="Submit"
            />
          )}
        </>
      )}
    </Box>
  );
};

export default MetalRewardsList;
